/**
 * receiptItems — as linhas discriminadas do RECIBO (lacuna L1).
 *
 * ─── A REGRA QUE DEFINE A FEATURE ───────────────────────────────────────────
 *
 * **Itens são OPCIONAIS.** Recibo sem itens continua sendo `amount` digitado à
 * mão e sai no PDF exatamente como sempre saiu — todo recibo já emitido tem
 * zero linhas, então o histórico não muda de aparência (a mesma disciplina do
 * título #202, da garantia #104 e do QR PIX #73).
 *
 * ─── POR QUE A CONTA É A DO ORÇAMENTO, E NÃO UMA NOVA ───────────────────────
 *
 * A linha do recibo tem a MESMA forma da linha do orçamento (quantidade, preço
 * unitário, desconto em valor ou percentual), então ela usa `quoteItemLineTotal`
 * de `src/lib/quoteTotals.ts`. Escrever uma segunda implementação aqui seria
 * exatamente o que o #69 documenta como a origem do pior defeito possível: duas
 * cópias da fórmula divergem na primeira correção, e aí o PDF imprime um total
 * diferente do que a tela mostra e do que o banco guardou.
 *
 * O que o recibo NÃO tem, de propósito: frete e desconto de CABEÇALHO. Recibo
 * comprova o que foi PAGO; um desconto de cabeçalho aqui seria uma segunda
 * maneira de dizer o mesmo número, e o `amount` já é a fonte de verdade.
 *
 * ─── `receipts.amount` CONTINUA SENDO A FONTE DE VERDADE ────────────────────
 *
 * Ela é coluna ARMAZENADA e alimenta a lista, o relatório de documentos e o
 * card do Dashboard. Havendo itens, ela é RECALCULADA a partir deles no save
 * (`receiptAmountFrom`); não havendo, é o valor digitado. Somar em tempo de
 * render deixaria a coluna obsoleta no instante em que alguém editasse um item
 * — o argumento do #76, aplicado aqui.
 *
 * ─── SEM `unit_cost` ────────────────────────────────────────────────────────
 *
 * Custo é snapshot do documento COMERCIAL (orçamento/OS) e alimenta o CMV. O
 * recibo é comprovante de PAGAMENTO: se ele carregasse custo, o mesmo serviço
 * entraria duas vezes no relatório de lucro.
 */
import { quoteItemLineTotal, type QuoteDiscountType } from '@/lib/quoteTotals';
import { roundQuantity } from '@/lib/quantity';

/** O item como o FORMULÁRIO o conhece (id local, para arrastar e editar). */
export interface ReceiptItemForm {
  id: string;
  name: string;
  description?: string | null;
  quantity: number;
  unit?: string | null;
  unit_price: number;
  discount?: number;
  discount_type?: QuoteDiscountType;
  product_id?: string | null;
}

/** A linha como ela vive no banco. */
export interface ReceiptItemRow {
  product_id: string | null;
  name: string;
  description: string | null;
  quantity: number;
  unit: string | null;
  unit_price: number;
  discount: number;
  discount_type: QuoteDiscountType;
  total: number;
  position: number;
}

const money = (raw: unknown): number => {
  const n = typeof raw === 'string' ? Number(raw.replace(',', '.')) : Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const normalizeDiscountType = (raw: unknown): QuoteDiscountType =>
  raw === 'percent' ? 'percent' : 'value';

/**
 * O total de UMA linha — delega para a conta do orçamento.
 *
 * Não reimplemente: `quoteItemLineTotal` já clampa quantidade e preço em zero
 * (#123b — uma linha negativa abateria o valor das outras sem aparecer como
 * desconto em lugar nenhum) e já arredonda em centavos POR LINHA, que é o que
 * faz a soma fechar com a coluna impressa no PDF (#69).
 */
export function receiptItemLineTotal(item: {
  quantity: unknown;
  unit_price: unknown;
  discount?: unknown;
  discount_type?: unknown;
}): number {
  return quoteItemLineTotal({
    quantity: item.quantity as number,
    unit_price: item.unit_price as number,
    discount: (item.discount ?? 0) as number,
    discount_type: normalizeDiscountType(item.discount_type),
  });
}

/**
 * O valor do recibo a partir das linhas. `null` quando não há linha nenhuma —
 * e `null` significa "não mexa no `amount`", nunca "o recibo vale zero".
 *
 * A distinção é o coração da feature: colapsar as duas coisas zeraria o valor
 * de TODO recibo do histórico (que não tem itens) na primeira edição.
 */
export function receiptAmountFrom(
  items: readonly { quantity: unknown; unit_price: unknown; discount?: unknown; discount_type?: unknown }[] | null | undefined,
): number | null {
  if (!items || items.length === 0) return null;
  const total = items.reduce((acc, item) => acc + receiptItemLineTotal(item), 0);
  return Math.round(total * 100) / 100;
}

/** Gravador: a linha do formulário vira a linha do banco. */
export function buildReceiptItemRow(item: ReceiptItemForm, position: number): ReceiptItemRow {
  const quantity = roundQuantity(item.quantity);
  const unitPrice = money(item.unit_price);
  const discount = money(item.discount);
  const discountType = normalizeDiscountType(item.discount_type);

  return {
    // Um `product_id` temporário da fila offline NUNCA pode chegar ao Postgres
    // (`invalid input syntax for type uuid`, e a fila inteira trava — #123d).
    product_id:
      typeof item.product_id === 'string' && !item.product_id.startsWith('offline_')
        ? item.product_id
        : null,
    name: (item.name ?? '').trim() || 'Item',
    description: item.description?.trim() || null,
    quantity,
    unit: item.unit?.trim() || null,
    unit_price: unitPrice,
    discount,
    discount_type: discountType,
    total: receiptItemLineTotal({
      quantity,
      unit_price: unitPrice,
      discount,
      discount_type: discountType,
    }),
    position,
  };
}

/** Leitor: da linha do banco (ou do cache) para o formato do formulário. */
export function receiptItemFromRow(row: Record<string, any>, index: number): ReceiptItemForm {
  return {
    id: String(row?.id ?? `receipt-item-${index}`),
    name: row?.name ?? '',
    description: row?.description ?? '',
    quantity: roundQuantity(Number(row?.quantity ?? 1)),
    unit: row?.unit ?? null,
    unit_price: Number(row?.unit_price ?? 0),
    discount: Number(row?.discount ?? 0),
    discount_type: normalizeDiscountType(row?.discount_type),
    product_id: row?.product_id ?? null,
  };
}

/**
 * As linhas do recibo no formato que o PDF consome (`PDFQuoteItem`).
 *
 * Existe para os DOIS produtores de PDF de recibo (a tela de detalhe e a
 * exportação em massa) usarem a mesma conversão: montada à mão nos dois, ela
 * divergiria na primeira correção — e o sintoma seria o PDF individual e o
 * exportado em lote imprimindo tabelas diferentes do MESMO recibo.
 *
 * Linha vazia devolve `[]`, e `[]` faz o template não desenhar tabela nenhuma.
 */
export function receiptPdfItems(receipt: Record<string, any> | null | undefined): Array<{
  name: string;
  description?: string | null;
  quantity: number;
  unit_price: number;
  discount: number;
  discount_type: QuoteDiscountType;
  unit?: string | null;
}> {
  const rows = Array.isArray(receipt?.receipt_items) ? receipt.receipt_items : [];
  return [...rows]
    .sort((a: any, b: any) => Number(a?.position ?? 0) - Number(b?.position ?? 0))
    .map((row: any) => ({
      name: row?.name ?? '',
      description: row?.description ?? null,
      quantity: Number(row?.quantity ?? 1),
      unit_price: Number(row?.unit_price ?? 0),
      discount: Number(row?.discount ?? 0),
      discount_type: normalizeDiscountType(row?.discount_type),
      unit: row?.unit ?? null,
    }));
}

/**
 * O rótulo de "forma de pagamento" impresso no recibo.
 *
 * ⚠️ NO RECIBO, `payment_methods` (plural) significa **as formas USADAS**, não
 * "as aceitas" como no orçamento. A distinção existe porque um pagamento pode
 * ser DIVIDIDO — R$ 300 no Pix e R$ 200 em dinheiro —, e com a coluna singular
 * sozinha isso é impossível de registrar.
 *
 * Precedência: a lista, se houver; senão a forma singular (o campo histórico).
 * Fonte única porque os DOIS produtores de PDF (tela e exportação em massa)
 * imprimem esse rótulo, e montá-lo à mão nos dois faria o mesmo recibo sair
 * diferente no lote — o modo de falha do #119.
 */
export function receiptPaymentLabel(
  receipt: Record<string, any> | null | undefined,
  labels: Record<string, string>,
): string | null {
  const lista = Array.isArray(receipt?.payment_methods) ? receipt.payment_methods : [];
  const rotulos = lista
    .map((m: unknown) => (typeof m === 'string' ? labels[m] ?? m : null))
    .filter((m: string | null): m is string => Boolean(m));
  if (rotulos.length > 0) return rotulos.join(' + ');

  const single = receipt?.payment_method;
  return single ? labels[single] ?? String(single) : null;
}
